import React, { useRef, useEffect, useState } from 'react';
import { GridType, FontItem } from '../types';

interface WorksheetProps {
  text: string;
  cols: number;
  gridType: GridType;
  currentFont: FontItem;
}

const WORKSHEET_PADDING = 40; // 20px each side (p-5)
const GRID_GAP = 12; // gap-3 = 12px
const PROBE_FONT_SIZE = 100; // Use a large probe size for accurate ratio measurement

/**
 * Measure the actual bounding box of a character by rendering it on a canvas
 * and scanning pixel data. This catches glyphs that extend outside the em square.
 */
function measureCharBounds(char: string, fontFamily: string, fontSize: number): { width: number; height: number } {
  const canvas = document.createElement('canvas');
  const size = Math.ceil(fontSize * 1.2); // Extra space for glyphs that extend beyond em square
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d', { willReadFrequently: true })!;

  ctx.fillStyle = 'white';
  ctx.fillRect(0, 0, size, size);
  ctx.fillStyle = 'black';
  ctx.font = `${fontSize}px "${fontFamily}", sans-serif`;
  ctx.textBaseline = 'top';
  ctx.fillText(char, 0, 0);

  const imageData = ctx.getImageData(0, 0, size, size);
  const data = imageData.data;
  let minX = size, maxX = 0, minY = size, maxY = 0;
  let hasPixels = false;

  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const alpha = data[(y * size + x) * 4 + 3];
      if (alpha > 10) {
        hasPixels = true;
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
    }
  }

  if (!hasPixels) {
    return { width: 0, height: 0 };
  }

  return {
    width: maxX - minX + 1,
    height: maxY - minY + 1,
  };
}

/**
 * Calculate the optimal font size so that every character fits within
 * the available cell size.
 */
function calcFontSize(
  chars: string[],
  fontFamily: string,
  containerWidth: number,
  cols: number
): number {
  const availableWidth = (containerWidth - WORKSHEET_PADDING - GRID_GAP * (cols - 1)) / cols;
  const targetSize = availableWidth * 0.95; // 95% of cell — character fills the grid

  let minRatio = Infinity;

  for (const char of chars) {
    const bounds = measureCharBounds(char, fontFamily, PROBE_FONT_SIZE);
    if (bounds.width === 0 && bounds.height === 0) continue; // skip empty/space

    const charSize = Math.max(bounds.width, bounds.height);
    const ratio = targetSize / charSize;
    if (ratio < minRatio) {
      minRatio = ratio;
    }
  }

  if (minRatio === Infinity) return Math.floor(targetSize);

  const fontSize = Math.floor(PROBE_FONT_SIZE * minRatio);
  return Math.max(12, Math.min(fontSize, Math.floor(targetSize)));
}

const Worksheet: React.FC<WorksheetProps> = ({ text, cols, gridType, currentFont }) => {
  const chars = text.split('');
  const containerRef = useRef<HTMLDivElement>(null);
  const [fontSize, setFontSize] = useState<number>(48);

  useEffect(() => {
    if (!containerRef.current || chars.length === 0) return;

    const containerWidth = containerRef.current.offsetWidth;
    if (containerWidth === 0) return;

    document.fonts.ready.then(() => {
      const optimal = calcFontSize(chars, currentFont.family, containerWidth, cols);
      setFontSize(optimal);
    });
  }, [text, cols, currentFont.family]);

  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden">
      <div className="px-4 py-2 border-b border-gray-100">
        <span className="text-xs text-gray-500">字帖预览（可滚动）</span>
      </div>
      <div ref={containerRef} className="h-[420px] overflow-y-auto scroll-hide p-5">
        <div
          className="grid gap-3"
          data-worksheet-grid
          style={{
            gridTemplateColumns: `repeat(${cols}, 1fr)`,
            fontFamily: `"${currentFont.family}", sans-serif`,
          }}
        >
          {chars.map((char, index) => (
            <div
              key={index}
              data-grid-cell
              className={`aspect-square flex items-center justify-center bg-white grid-${gridType} overflow-hidden`}
              style={{ fontSize: `${fontSize}px`, lineHeight: 1 }}
            >
              {char}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Worksheet;
